// The realms, as washes of pigment. Each realm is cut into rectangles ("cells") that never overlap; a
// rectangle stays a rectangle however the lands and years are stretched, so nothing tears or overlaps while
// the camera moves. The hand-drawn edge, the pooling of pigment along it and the paper seam between two
// washes are all drawn in the fragment shader, from each cell's distance to the realm's outer edges.

import type { Block, Chart, Realm } from "@hoee/chart";
import {
  DoubleSide,
  GLSL3,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  PlaneGeometry,
  RawShaderMaterial,
} from "three";
import type { IUniform, DataTexture } from "three";
import { EDITION, MAPPING, NOISE } from "./glsl.ts";
import { PAPER_SAMPLE } from "./paper.ts";
import type { SharedUniforms } from "./stage.ts";

/** Which sides of a cell are the realm's outer edge, and how the cell is painted. */
/** Which sides of a cell are the realm's outer edge (in lane and time terms), and how it is painted. */
export const CellFlag = { LaneStart: 1, TimeStart: 2, LaneEnd: 4, TimeEnd: 8, Partial: 16, FadesIn: 32 } as const;

export interface Cell {
  realm: Realm;
  block: Block;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  flags: number;
}

/** Cuts a realm into cells along every edge of its blocks. */
export function cellsOf(realm: Realm): Cell[] {
  const xs = [...new Set(realm.blocks.flatMap((b) => [b.x0, b.x1]))].sort((a, b) => a - b);
  const ys = [...new Set(realm.blocks.flatMap((b) => [b.cy0, b.y1]))].sort((a, b) => a - b);
  const blockAt = (i: number, j: number): Block | undefined => {
    if (i < 0 || j < 0 || i >= xs.length - 1 || j >= ys.length - 1) return undefined;
    const cx = ((xs[i] as number) + (xs[i + 1] as number)) / 2,
      cy = ((ys[j] as number) + (ys[j + 1] as number)) / 2;
    return realm.blocks.find((b) => b.x0 < cx && cx < b.x1 && b.cy0 < cy && cy < b.y1);
  };
  const cells: Cell[] = [];
  for (let j = 0; j < ys.length - 1; j++) {
    for (let i = 0; i < xs.length - 1; i++) {
      const block = blockAt(i, j);
      if (!block) continue;
      let flags = 0;
      if (!blockAt(i - 1, j)) flags |= CellFlag.LaneStart;
      if (!blockAt(i, j - 1)) flags |= CellFlag.TimeStart;
      if (!blockAt(i + 1, j)) flags |= CellFlag.LaneEnd;
      if (!blockAt(i, j + 1)) flags |= CellFlag.TimeEnd;
      if (block.partial) flags |= CellFlag.Partial;
      if (flags & CellFlag.TimeStart && (block.from == null || block.y0 < block.cy0)) flags |= CellFlag.FadesIn;
      cells.push({
        realm,
        block,
        x0: xs[i] as number,
        x1: xs[i + 1] as number,
        y0: ys[j] as number,
        y1: ys[j + 1] as number,
        flags,
      });
    }
  }
  return cells;
}

// Cells are stored in lane and time terms; on screen, time runs across and lanes down. Screen sides:
// left = TimeStart (2), top = LaneStart (1), right = TimeEnd (8), bottom = LaneEnd (4).
const VERTEX = /* glsl */ `
precision highp float;
${MAPPING}
uniform float uPad;        // px the quad grows on outer sides (room for the wobble, or a shadow)
in vec3 position;          // unit quad, 0..1
in vec2 aX;                // lane units: first, last
in vec2 aU;                // time: start, end (u)
in vec3 aMeta;             // pigment, flags, realm index
out vec2 vPx;
out vec2 vWorld;           // centuries across, lane units down
flat out vec4 vRect;       // the cell in px: x0, y0, x1, y1
flat out vec4 vLines;      // its sides in world terms: time start, time end (centuries), lane start, lane end
flat out vec3 vMeta;
flat out float vLanePx;    // px per lane unit here
void main() {
  float x0 = timeX(aU.x), x1 = timeX(aU.y);
  float y0 = laneY(aX.x), y1 = laneY(aX.y);
  int flags = int(aMeta.y);
  vec2 lo = vec2(x0 - ((flags & 2) != 0 ? uPad : 0.0), y0 - ((flags & 1) != 0 ? uPad : 0.0));
  vec2 hi = vec2(x1 + ((flags & 8) != 0 ? uPad : 0.0), y1 + ((flags & 4) != 0 ? uPad : 0.0));
  vec2 p = mix(lo, hi, position.xy);
  vPx = p;
  vRect = vec4(x0, y0, x1, y1);
  vec2 t = (p - vec2(x0, y0)) / max(vec2(1e-3), vec2(x1 - x0, y1 - y0));
  vWorld = vec2(mix(aU.x, aU.y, t.x) * 50.0, mix(aX.x, aX.y, t.y));
  vLines = vec4(aU.xy * 50.0, aX);
  vMeta = aMeta;
  vLanePx = (y1 - y0) / max(1e-3, aX.y - aX.x);
  gl_Position = toClip(p);
}`;

/** Signed distance (px) inside the realm's hand-drawn outline: positive inside, negative outside. */
const OUTLINE = /* glsl */ `
float outline(vec2 px) {
  int flags = int(vMeta.y);
  float w = vRect.z - vRect.x, h = vRect.w - vRect.y;
  float amp = min(1.4, 0.1 * min(w, h));
  float zy = log2(max(1e-3, vLanePx / 36.0));
  float big = 1e5;
  float dl = (flags & 2) != 0 ? px.x - (vRect.x + amp * wobbleAtZoom(vWorld.y / 0.35, vLines.x * 7.31, zy)) : big;
  float dr = (flags & 8) != 0 ? (vRect.z + amp * wobbleAtZoom(vWorld.y / 0.35, vLines.y * 7.31, zy)) - px.x : big;
  float dt = (flags & 1) != 0 ? px.y - (vRect.y + amp * wobbleAtZoom(vWorld.x / 0.4, vLines.z * 3.17, uTimeZoom)) : big;
  float db = (flags & 4) != 0 ? (vRect.w + amp * wobbleAtZoom(vWorld.x / 0.4, vLines.w * 3.17, uTimeZoom)) - px.y : big;
  float d = min(min(dl, dr), min(dt, db));
  float r = min(6.0, 0.45 * min(w, h));
  if (dl < r && dt < r) d = min(d, r - length(vec2(r - dl, r - dt)));
  if (dr < r && dt < r) d = min(d, r - length(vec2(r - dr, r - dt)));
  if (dl < r && db < r) d = min(d, r - length(vec2(r - dl, r - db)));
  if (dr < r && db < r) d = min(d, r - length(vec2(r - dr, r - db)));
  return d;
}
`;

const FRAGMENT_HEAD = /* glsl */ `
precision highp float;
${MAPPING}
${EDITION}
${NOISE}
${PAPER_SAMPLE}
uniform float uTimeZoom;
uniform float uFocus;
uniform float uFocusMix;
uniform float uHover;
uniform float uLift;
uniform float uOpacity;
in vec2 vPx;
in vec2 vWorld;
flat in vec4 vRect;
flat in vec4 vLines;
flat in vec3 vMeta;
flat in float vLanePx;
out vec4 fragColor;
${OUTLINE}
`;

const FRAGMENT = /* glsl */ `
${FRAGMENT_HEAD}
void main() {
  int flags = int(vMeta.y);
  float d = outline(vPx);
  // A hairline of paper between washes by day; dark lead between panes by night.
  float seam = mix(0.6, 1.0, uNight);
  float coverage = smoothstep(seam - 0.6, seam + 0.6, d);
  if (coverage <= 0.0) discard;

  vec3 paper = paperAt(vPx);
  vec3 col = pigment(vMeta.x);
  // Day: an even wash, granulating into the paper's tooth, pooling a little darker along its edge.
  vec3 day = col * (1.0 - 0.045 * paper.g) * (1.0 - 0.12 * paper.r);
  day = mix(day, poolTone(), 0.16 * (1.0 - smoothstep(0.0, 6.0, d - seam)));
  // Night: deep glass, lit from behind, brightest at its heart.
  vec3 night = col * (0.72 + 0.22 * paper.b) * (0.86 + 0.14 * smoothstep(0.0, 18.0, d - seam));
  vec3 c = mix(day, night, uNight);

  // The paper's grain catches the light that follows the pointer.
  vec2 e = vec2(1.5, 0.0);
  vec2 n = vec2(paperAt(vPx + e.xy).r - paperAt(vPx - e.xy).r, paperAt(vPx + e.yx).r - paperAt(vPx - e.yx).r) * 0.9;
  c *= 1.0 + 0.16 * raking(vPx, n);

  if ((flags & 16) != 0) {
    // Partial rule: hatched.
    float s = fract((vPx.x - vPx.y) / 8.0);
    c = mix(c, gapTone(), step(0.62, s) * 0.8);
  }
  float alpha = mix(0.94, 1.0, uNight) * coverage;
  // Realms with no printed start fade in from the left.
  if ((flags & 32) != 0) alpha *= smoothstep(0.0, min(90.0, 0.5 * (vRect.z - vRect.x)), vPx.x - vRect.x);
  // The rest of the world steps back while you are inside a realm.
  float other = (uFocus >= 0.0 && abs(vMeta.z - uFocus) > 0.5) ? uFocusMix : 0.0;
  c = mix(c, mix(paperTone(), c, 0.5), other * 0.5);
  // The realm under the pointer lifts towards the light.
  if (abs(vMeta.z - uHover) < 0.5) c *= 1.0 + 0.05 * uLift;
  // The rolled ends of the scroll turn away into shadow.
  float r = rollDepth(vPx.x);
  c *= 1.0 - mix(0.1, 0.2, uNight) * r * r;
  fragColor = vec4(c, alpha * uOpacity);
}`;

/** The soft shadow under a lifted realm: drawn beneath the washes, only for the realm being lifted. */
const SHADOW = /* glsl */ `
${FRAGMENT_HEAD}
void main() {
  // The realm under the pointer lifts a little; an opened realm floats above the page.
  float lift = abs(vMeta.z - uHover) < 0.5 ? uLift : abs(vMeta.z - uFocus) < 0.5 ? uFocusMix * 1.15 : 0.0;
  if (lift < 0.01) discard;
  vec2 offset = vec2(0.0, 6.0) * lift;
  float blur = 4.0 + 14.0 * lift;
  float d = outline(vPx - offset);
  float a = 0.26 * lift * smoothstep(-blur, blur * 0.4, d) * (1.0 - rollDepth(vPx.x));
  if (a < 0.003) discard;
  fragColor = vec4(mix(vec3(0.16, 0.1, 0.04), vec3(0.0), uNight), a * mix(1.0, 1.6, uNight) * uOpacity);
}`;

export interface WashUniforms {
  [name: string]: IUniform;
  uFocus: IUniform<number>;
  uFocusMix: IUniform<number>;
  uHover: IUniform<number>;
  uLift: IUniform<number>;
  uOpacity: IUniform<number>;
}

export interface WashLayer {
  mesh: Mesh;
  /** The lifted realm's shadow, drawn under the washes. */
  shadow: Mesh;
  uniforms: WashUniforms;
}

/** Instanced washes for a list of cells, in one draw call. */
function washLayer(
  cells: readonly Omit<Cell, "block" | "realm">[],
  meta: (i: number) => [pigment: number, realm: number],
  chart: Chart,
  shared: SharedUniforms,
  paper: DataTexture,
): WashLayer {
  const time = chart.scales.true;
  const n = cells.length;
  const aX = new Float32Array(n * 2),
    aU = new Float32Array(n * 2),
    aMeta = new Float32Array(n * 3);
  cells.forEach((c, i) => {
    aX.set([c.x0, c.x1], i * 2);
    aU.set([time.toU(c.y0), time.toU(c.y1)], i * 2);
    const [pigment, realm] = meta(i);
    aMeta.set([pigment, c.flags, realm], i * 3);
  });
  const quad = new PlaneGeometry(1, 1).translate(0.5, 0.5, 0);
  const geometry = new InstancedBufferGeometry();
  geometry.index = quad.index;
  geometry.setAttribute("position", quad.getAttribute("position"));
  geometry.setAttribute("aX", new InstancedBufferAttribute(aX, 2));
  geometry.setAttribute("aU", new InstancedBufferAttribute(aU, 2));
  geometry.setAttribute("aMeta", new InstancedBufferAttribute(aMeta, 3));
  geometry.instanceCount = n;
  const own: WashUniforms = {
    uFocus: { value: -1 },
    uFocusMix: { value: 0 },
    uHover: { value: -1 },
    uLift: { value: 0 },
    uOpacity: { value: 1 },
  };
  const material = new RawShaderMaterial({
    glslVersion: GLSL3,
    uniforms: { ...shared, ...own, uPaperTex: { value: paper } },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    // Screen space runs y down, which turns the quads over.
    side: DoubleSide,
  });
  const mesh = new Mesh(geometry, material);
  mesh.frustumCulled = false;
  const shadowMaterial = material.clone();
  shadowMaterial.uniforms = { ...shared, ...own, uPaperTex: { value: paper }, uPad: { value: 26 } };
  shadowMaterial.fragmentShader = SHADOW;
  const shadow = new Mesh(geometry, shadowMaterial);
  shadow.frustumCulled = false;
  shadow.renderOrder = -1;
  return { mesh, shadow, uniforms: own };
}

/** Every realm on the chart. */
export function realmsLayer(chart: Chart, shared: SharedUniforms, paper: DataTexture): WashLayer & { cells: Cell[] } {
  const cells = chart.realms.flatMap((r) => cellsOf(r));
  const layer = washLayer(
    cells,
    (i) => {
      const r = (cells[i] as Cell).realm;
      return [r.color, r.index];
    },
    chart,
    shared,
    paper,
  );
  return { ...layer, cells };
}
